import { useDialog } from '@repo/ui';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/router';
import { GraphQLTaggedNode, useMutation } from 'react-relay';

import { employmentUpdateMutation, employmentUpdateMutation$data, employmentUpdateMutation$variables } from '@/__generated__/employmentUpdateMutation.graphql';
import { useSurveyErrorDialogAttributes } from '@/components/survey/common/hooks/use-survey-error-dialog-attributes';
import { SURVEY_PAGES } from '@/lib/constants/paths';
import { surveyAnswerIdAtom } from '@/lib/stores/survey/common';

type UpsertEmploymentFunction = (params: {
  variables: Omit<employmentUpdateMutation$variables, 'surveyAnswerId' | 'isCompleted'>;
  onCompleted: (res: employmentUpdateMutation$data) => void;
  onError?: () => void;
}) => void;

export const useUpsertEmployment = (mutation: GraphQLTaggedNode): [UpsertEmploymentFunction, boolean] => {
  const router = useRouter();
  const [commit, isLoading] = useMutation<employmentUpdateMutation>(mutation);
  const surveyAnswerId = useAtomValue(surveyAnswerIdAtom);
  const { showDialog } = useDialog();
  const { alreadyCompleted, invalidSurveyData } = useSurveyErrorDialogAttributes();

  const showErrorDialog = () => {
    showDialog({
      title: '일시적인 오류가 발생했어요.\n잠시 후 다시 시도해주세요.'
    });
  };

  const upsertEmployment: UpsertEmploymentFunction = ({ variables, onCompleted, onError }) => {
    if (!surveyAnswerId) {
      showErrorDialog();
      return;
    }
    commit({
      variables: Object.assign({}, variables, {
        surveyAnswerId,
        isCompleted: false
      }),
      onCompleted: (res, errors) => {
        if (errors || !res?.upsertEmploymentAnswer?.result || !!res?.upsertEmploymentAnswer?.errors) {
          const surveyError = res.upsertEmploymentAnswer.errors;
          switch (surveyError?.code) {
            case 'ERR_SURVEY_EXPIRED': {
              router.push(SURVEY_PAGES.만료);
              return;
            }
            case 'ERR_SURVEY_COMPLETED': {
              showDialog(alreadyCompleted);
              return;
            }
            case 'ERR_UPDATE_SURVEY_ANSWER': {
              showDialog(invalidSurveyData);
              return;
            }
          }
          showErrorDialog();
          return;
        }

        onCompleted(res);
      },
      onError: () => {
        if (onError) {
          onError?.();
          return;
        }
        showErrorDialog();
      }
    });
  };

  return [upsertEmployment, isLoading];
};
